import { mat4, vec3 } from 'gl-matrix';
import { Ray, toRadian, unproject } from '../math';
import { ViewFrustum } from '../util';

export abstract class Camera {
  public name: string = '';
  public fov: number = 45;
  public near: number = 0.1;
  public far: number = 1000;

  private readonly _baseInvViewProjection: mat4 = mat4.create();
  private readonly _baseInvProjection: mat4 = mat4.create();
  private readonly _baseUnjitteredVP: mat4 = mat4.create();

  /**
   * Marks whether the previous-frame matrix cache and any accumulated temporal
   * history (TAA, future SSAO/volumetric history) is valid for blending into
   * this frame. Cleared on first frame, scene change, or large camera teleport.
   */
  public historyValid = false;

  /**
   * If true, `applyProjection()` should add a Halton sub-pixel offset to the
   * projection matrix. Toggled by the TAA pass enable/disable.
   */
  public taaJitterEnabled = false;

  /**
   * Per-frame index used by jittered effects that ride on TAA's accumulation
   * (PCF shadow disk rotation, SSAO noise rotation, ...). 0 means "no jitter"
   * — sample with the canonical pattern; nonzero advances the rotation so
   * consecutive frames sample different angles and TAA averages them into
   * supersampled output.
   *
   * Base implementation returns 0 (cameras that don't drive TAA — e.g. light
   * cameras — never jitter). DefaultCamera overrides with the live counter.
   */
  public get taaFrameIndex(): number {
    return 0;
  }

  constructor(public dimensions: { width: number; height: number }) {}

  public invalidateHistory(): void {
    this.historyValid = false;
  }

  public getRay(x: number, y: number): Ray {
    let near = unproject(
      vec3.fromValues(x, y, 0),
      this.projectionMatrix,
      this.modelViewMatrix,
      this.dimensions,
    );

    let far = unproject(
      vec3.fromValues(x, y, 1),
      this.projectionMatrix,
      this.modelViewMatrix,
      this.dimensions,
    );

    return new Ray(
      vec3.fromValues(near[0], near[1], near[2]),
      vec3.fromValues(far[0], far[1], far[2]),
    );
  }

  public get aspectRatio(): number {
    return this.dimensions.width / this.dimensions.height;
  }

  protected applyPerspective(matrix: mat4, fov: number = 45): void {
    mat4.perspective(matrix, toRadian(fov), this.aspectRatio, this.near, this.far);
  }

  protected applyOrtho(matrix: mat4, scale: number): void {
    mat4.ortho(matrix, -scale, scale, -scale, scale, this.near, this.far);
  }

  public abstract get modelViewMatrix(): mat4;

  public abstract get projectionMatrix(): mat4;

  /** Inverse of (projection * view). Used to reconstruct world position from depth.
   *  Default impl recomputes every read; subclasses with hot per-frame use should cache. */
  public get invViewProjection(): mat4 {
    mat4.mul(this._baseInvViewProjection, this.projectionMatrix, this.modelViewMatrix);
    mat4.invert(this._baseInvViewProjection, this._baseInvViewProjection);
    return this._baseInvViewProjection;
  }

  /** Inverse of the projection matrix. Used to reconstruct view-space position from depth. */
  public get invProjection(): mat4 {
    mat4.invert(this._baseInvProjection, this.projectionMatrix);
    return this._baseInvProjection;
  }

  /**
   * Projection matrix WITHOUT TAA jitter. Used by motion-vector computation —
   * jitter must not contaminate motion vectors. Light/impostor cameras don't
   * jitter so the default impl just aliases `projectionMatrix`. Subclasses
   * that jitter (DefaultCamera) override with the unjittered cached value.
   */
  public get unjitteredProjection(): mat4 {
    return this.projectionMatrix;
  }

  /**
   * `unjitteredProjection × modelView`. Cached in DefaultCamera; the base
   * fallback recomputes per call.
   */
  public get unjitteredViewProjection(): mat4 {
    return mat4.mul(this._baseUnjitteredVP, this.unjitteredProjection, this.modelViewMatrix);
  }

  /**
   * The previous frame's `unjitteredViewProjection`, captured at the start of
   * `prepareFrame()` before any matrix updates. Used by motion-vector
   * computation (`prevVP × currentWorldPos` → previous-frame screen position).
   * Default fallback (for cameras that aren't motion sources, e.g. light
   * cameras) just returns the current value — they won't be sampled for motion.
   */
  public get previousViewProjection(): mat4 {
    return this.unjitteredViewProjection;
  }

  public abstract get targetPosition(): vec3 | number[];

  public abstract get cameraPosition(): vec3 | number[];

  public abstract get frustum(): ViewFrustum;

  public abstract prepareFrame(): void;
}
